// `extension-alert-candidate-snippet` — the candidate profile itself: a header with the
// avatar, name, "View in ATS" and the action icons, over a scrolling column of highlight cards.
//
// ⚠️ THE HEADER MOVES BETWEEN TWO PLACES RATHER THAN CHANGING SHAPE. In the narrow ("Less")
// state the contact lines — location, email, phone — sit INSIDE the scrolling column above the
// cards; in the wide ("More") state they move OUT into the header, under a divider, and the
// header lays itself out as a row. Both copies exist in the template as separate `ng-if`s over
// the same `ng-include`, which is why this renders one fragment in two positions instead of
// repositioning one with CSS.
import { createElement, useState, type CSSProperties, type ReactElement, type ReactNode } from 'react'
import { ProfileActionsStrip } from './ProfileActionsStrip.tsx'
import { AtsInfoCard, type AtsInfo } from './AtsInfoCard.tsx'
import { MatchingSummaryCard } from './MatchingSummaryCard.tsx'
import {
  ProfileEducationSection,
  ProfileExperienceSection,
  ProfileSkillsSection,
  type ProfilePreviewData,
} from './ProfilePreviewSections.tsx'
import { candidateName, type NamedEntity } from './candidateName.ts'
import type { EnrichableEntity } from './enrichment.ts'
import type { ProfileActionDefinition } from './profileActions.ts'

/** `IAlertCandidateUrlsDto`, plus the icon and analytics type the controller pairs with each. */
export interface SocialUrls {
  linkedinUrl?: string | null
  githubUrl?: string | null
  dribbleUrl?: string | null
}

export interface SocialUrlDisplay {
  url: string
  iconClass: string
  type: string
}

/**
 * `initSocialUrlsDisplay` — the three social circles beside "View in ATS".
 *
 * ⚠️ LINKEDIN HIDES ITSELF WHEN THE USER IS ALREADY ON LINKEDIN, and only outside hunter:
 * `!viaHunter && host === Linkedin`. GitHub and Dribbble have no such rule. The order is fixed
 * — LinkedIn, GitHub, Dribbble — and every entry without a URL is dropped at the end, so the
 * filter cannot be folded into the pushes without changing which icons survive.
 *
 * ⚠️ AND THE SPELLING IS `dribbleUrl`, ONE B. The site is Dribbble; the DTO field is not.
 * Correcting it here silently drops the icon.
 */
export function socialUrlsDisplay(
  socialUrls: SocialUrls | undefined | null,
  options: { viaHunter?: boolean; onLinkedin?: boolean } = {},
): SocialUrlDisplay[] {
  if (socialUrls === undefined || socialUrls === null) return []

  const entries: Array<{ url?: string | null; iconClass: string; type: string }> = []
  const hideLinkedin = options.viaHunter !== true && options.onLinkedin === true
  if (!hideLinkedin) entries.push({ url: socialUrls.linkedinUrl, iconClass: 'fab fa-linkedin-in', type: 'linkedin' })
  entries.push({ url: socialUrls.githubUrl, iconClass: 'fab fa-github-alt', type: 'github' })
  entries.push({ url: socialUrls.dribbleUrl, iconClass: 'fab fa-dribbble', type: 'dribble' })

  return entries.filter(
    (entry): entry is SocialUrlDisplay => entry.url !== undefined && entry.url !== null && entry.url !== '',
  )
}

/**
 * `InitialsService.getNameInitials` — the two letters in the circle.
 *
 * ⚠️ IT SPLITS ON A SINGLE SPACE AND TAKES THE FIRST TWO PARTS, so a name with a middle name
 * gives the first and middle initial, not the first and last. And a name with NO space gives
 * ONE letter, not two — "Cher" is "C". Both are visible on the profile screen.
 */
export function nameInitials(name: string | null | undefined): string {
  if (name === undefined || name === null || name === '') return ''
  if (!name.includes(' ')) return name[0] ?? ''
  return name
    .split(' ')
    .slice(0, 2)
    .map((part) => part[0] ?? '')
    .join('')
}

/**
 * `getCandidateLastPositionInfo` — "Business Analyst @ KPMG".
 *
 * Either half alone stands on its own; the "@" only appears when both exist.
 */
export function lastPositionInfo(entity: { lastPosition?: string | null; lastCompany?: string | null }): string {
  const position = entity.lastPosition
  const company = entity.lastCompany
  if (position === undefined || position === null || position === '') {
    return company === undefined || company === null ? '' : company
  }
  if (company === undefined || company === null || company === '') return position
  return `${position} @ ${company}`
}

/** What the snippet needs of the candidate. A superset of the slim row's fields. */
export interface SnippetCandidate extends NamedEntity, EnrichableEntity {
  id: number
  lastPosition?: string | null
  lastCompany?: string | null
  location?: string | null
  email?: string | null
  phoneNumber?: string | null
  cvDate?: number | null
  favorite?: boolean | null
  hidden?: boolean | null
  socialUrls?: SocialUrls | null
  atsInfo?: AtsInfo | null
}

export interface CandidateSnippetProps {
  candidate?: SnippetCandidate
  previewData?: ProfilePreviewData
  /** False once the preview call settles, however it settles. */
  loadingSummaryAndPreview?: boolean
  actions?: readonly ProfileActionDefinition[]
  atsName?: string
  /** `ctrl.atsLabel` — every place the snippet NAMES the ATS (5cfd3ef899). Defaults to `atsName`. */
  atsLabel?: string
  relevantDaysForEnriched?: number
  /** `atsInfoFeatureFlagHelperService.isATSInfoEnabled()` — LinkedIn or Indeed search on. */
  atsInfoEnabled?: boolean
  /**
   * Whether the preview call returned a matching summary.
   *
   * It does two things, and the second is easy to miss: it renders the card (with `viaHunter`),
   * and it decides the empty state. "No highlights for this candidate" is gated on
   * `!matchingSummary && !previewData`, so a candidate with a summary and no preview shows an
   * empty highlights column rather than that sentence.
   */
  hasMatchingSummary?: boolean
  /** `ctrl.alert.name` — the search the profile was opened from; the card's "For … hunter". */
  alertName?: string
  /** `$stateParams.showEnrichmentNotification` — see the header CTA below. */
  showEnrichmentNotification?: boolean
  /** See `AtsInfoCard`: a session with no extension shell reports IN the ATS. */
  inAts?: boolean
  viaHunter?: boolean
  onLinkedin?: boolean
  /** `isInViewMoreState()` — the wide layout, which the "More" link routes to. */
  viewMore?: boolean
  hideShowMoreLessButton?: boolean
  maxSnippetWidth?: number
  onToggleViewMore?: () => void
  onViewInAts?: () => void
  onAction?: (action: ProfileActionDefinition) => void
  onSocialUrlClick?: (type: string) => void
  /** `on-scroll="ctrl.onScrollOnce($event)"` on the scrolling `.snippet-content` — every scroll event; the caller decides what counts. */
  onContentScroll?: (element: HTMLElement) => void
  /**
   * `max-width` on the HOST ELEMENT — `ExtensionViewWidth.Extended` on the profile state.
   *
   * ⚠️ MAX-WIDTH HERE, NOT FLEX-BASIS. The state binds `flex-basis` only when it is showing a
   * side-by-side child (add-to-job, messages, email sign-in) and leaves it `auto` otherwise,
   * while `max-width` is always the same number. That is the fifth distinct width recipe in
   * this app and, as with the others, none of it is in the stylesheet.
   */
  maxWidth?: number
  /** True on the three side-by-side children; swaps `flex-basis: auto` for `maxWidth`. */
  sideBySide?: boolean
  /** The transcluded side view — only rendered in the wide state. */
  children?: ReactNode
}

/**
 * ⚠️ THE ELEMENT IS STYLED BY TYPE, and it is styled together with its two siblings:
 * `extension-alert-candidate-snippet, extension-alert-external-candidate-snippet,
 * extension-alert-client-contact-snippet { display: flex; … }`. Nothing gives that rule a class
 * to hang on, so a React tree that renders only the inner `div.extension-alert-candidate-snippet`
 * loses `display: flex` on the box that holds the whole screen and the header stops laying out.
 */
function SnippetHost({
  maxWidth,
  sideBySide,
  children,
}: {
  maxWidth?: number
  sideBySide: boolean
  children: ReactNode
}): ReactElement {
  const style: CSSProperties = {}
  if (maxWidth !== undefined) {
    style.maxWidth = maxWidth
    style.flexBasis = sideBySide ? maxWidth : 'auto'
  }
  return createElement('extension-alert-candidate-snippet', { style }, children)
}

/** The three contact lines, rendered in one of two places. */
function SnippetContactInfo({
  candidate,
  socials,
}: {
  candidate: SnippetCandidate
  socials: readonly SocialUrlDisplay[]
}): ReactElement {
  const hasPhone =
    candidate.phoneNumber !== undefined && candidate.phoneNumber !== null && candidate.phoneNumber !== ''
  return (
    <div>
      {candidate.location === undefined || candidate.location === null || candidate.location === '' ? null : (
        <div className="candidate-contact-info-item">
          <i className="fa fa-map-marker fa-sm fa-fw" />
          &nbsp;{candidate.location}
        </div>
      )}
      {candidate.email === undefined || candidate.email === null || candidate.email === '' ? null : (
        <div className="candidate-contact-info-item">
          <i className="fas fa-envelope fa-sm fa-fw" />
          &nbsp;{candidate.email}
        </div>
      )}
      {/* ⚠️ THE ROW'S CONDITION IS PHONE **OR** SOCIALS, though the social half inside it is
          commented out upstream — the socials moved to the header and the outer `ng-if` was
          left behind. So a candidate with no phone and a LinkedIn URL renders an EMPTY row,
          which has padding and moves everything below it. Dropping the socials from the
          condition would be the tidy reading and would shift the card by a few pixels. */}
      {!hasPhone && socials.length === 0 ? null : (
        <div className="candidate-contact-info-item candidate-contact-info-phone-social">
          {!hasPhone ? null : (
            <div className="candidate-contact-info-phone">
              <i className="fas fa-mobile-alt fa-sm fa-fw" />
              &nbsp;{candidate.phoneNumber}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export function CandidateSnippet({
  candidate,
  previewData,
  loadingSummaryAndPreview = false,
  actions = [],
  atsName,
  atsLabel = atsName,
  alertName,
  relevantDaysForEnriched,
  atsInfoEnabled = false,
  hasMatchingSummary = false,
  showEnrichmentNotification = false,
  inAts = true,
  viaHunter = false,
  onLinkedin = false,
  viewMore = false,
  hideShowMoreLessButton = false,
  maxSnippetWidth,
  onToggleViewMore,
  onViewInAts,
  onAction,
  onSocialUrlClick,
  onContentScroll,
  maxWidth,
  sideBySide = false,
  children,
}: CandidateSnippetProps): ReactElement {
  // `canShowSnippetHeaderNotification` — the CTA's ✕ suppresses it for the life of the
  // component and nothing brings it back. Declared before the loading return so the hook order
  // is the same on both paths.
  const [ctaDismissed, setCtaDismissed] = useState(false)

  // The whole screen is a spinner until the candidate arrives — the header cannot draw a name
  // it does not have, and `getCandidateWithMinTimeForLoading` holds it for at least 500ms so
  // the spinner never flashes.
  if (candidate === undefined) {
    return (
      <SnippetHost maxWidth={maxWidth} sideBySide={sideBySide}>
        <div className="extension-alert-candidate-snippet">
          <div className="snippet-loading-container-outer">
            <div className="snippet-loading-container">
              <i className="fa fa-spinner fa-spin" />
            </div>
          </div>
        </div>
      </SnippetHost>
    )
  }

  const socials = socialUrlsDisplay(candidate.socialUrls, { viaHunter, onLinkedin })
  const positionInfo = lastPositionInfo(candidate)
  const contactInfo = <SnippetContactInfo candidate={candidate} socials={socials} />
  const hasHighlights = previewData !== undefined || hasMatchingSummary
  const showAtsInfo = candidate.atsInfo !== undefined && candidate.atsInfo !== null && atsInfoEnabled
  const experience = previewData?.experience ?? []
  const education = previewData?.education ?? []
  const skills = previewData?.skills ?? []

  return (
    <SnippetHost maxWidth={maxWidth} sideBySide={sideBySide}>
      <div className="extension-alert-candidate-snippet">
        <div className="candidate-alert-info">
          <div className={`candidate-snippet-header${viewMore ? ' row-view' : ''}`}>
            {/* ⚠️ THE CTA'S CONDITION IS THE OPPOSITE OF WHAT IT READS LIKE. It shows when the
              user is NOT in their ATS — "Candidate found on Bullhorn", telling them the record
              already exists — or when the router explicitly asked for the enrichment notice.
              In the ATS, with no such parameter, it is absent, which is every capture. */}
            {viewMore || ctaDismissed || (inAts && !showEnrichmentNotification) ? null : (
              <div className="snippet-extension-user-cta">
                <div className="snippet-extension-user-enrichment-cta">
                  {inAts && showEnrichmentNotification ? (
                    <span>New information is available for this candidate</span>
                  ) : null}
                  {!inAts ? <span>Candidate found on {atsLabel}</span> : null}
                </div>
                <div className="snippet-extension-user-cta-x">
                  <i
                    className="far fa-times cursor-pointer"
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setCtaDismissed(true)
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') setCtaDismissed(true)
                    }}
                  />
                </div>
              </div>
            )}
            <div
              className="candidate-basic-info-wrap"
              style={{ flexBasis: viewMore && maxSnippetWidth !== undefined ? maxSnippetWidth : 'auto' }}
            >
              <div className="candidate-image-wrap">
                {/* Initials in a circle. Upstream still carries the ui-avatars.com <img> it
                  replaced, commented out — do not restore it: it would put every candidate's
                  name into a third-party URL. */}
                <div className="candidate-image">{nameInitials(candidate.name)}</div>
              </div>
              <div className="candidate-basic-info-and-actions">
                <div className="candidate-basic-info">
                  <div className="info-wrap">
                    <div className="candidate-name" title={candidateName(candidate)}>
                      {candidateName(candidate)}
                    </div>
                    {positionInfo === '' ? null : (
                      <div className="candidate-last-job-title" title={positionInfo}>
                        {positionInfo}
                      </div>
                    )}
                  </div>
                  {hideShowMoreLessButton ? null : (
                    <div className="op-info-wrap">
                      <a
                        className={`snippet-action op-info snippet-action-btn ${viewMore ? 'op-less-info' : 'op-more-info'}`}
                        role="button"
                        tabIndex={0}
                        onClick={onToggleViewMore}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') onToggleViewMore?.()
                        }}
                      >
                        <span className="snippet-action-btn-text">{viewMore ? 'Less' : 'More'}</span>
                        &nbsp;
                        {/* `rtl-hidden` — the chevron is dropped rather than mirrored in RTL. */}
                        <i
                          className={`snippet-action-btn-icon fal rtl-hidden ${viewMore ? 'fa-chevron-left' : 'fa-chevron-right'}`}
                        />
                      </a>
                    </div>
                  )}
                </div>
                <div className="candidate-links-and-actions">
                  <div className="op-view-in-ats-container">
                    {/* ⚠️ ORANGE MEANS **NOT** IN THE ATS. The modifier is `!inATS`, so the
                      button is plain in the state a capture runs in and orange when the user
                      opened the frame somewhere else. */}
                    <button
                      type="button"
                      className={`op-view-in-ats cursor-pointer${inAts ? '' : ' view-in-ats-orange'}`}
                      onClick={onViewInAts}
                    >
                      View in ATS
                    </button>
                  </div>
                  {socials.length === 0 ? null : (
                    <div className="candidate-contact-info-social-url-container">
                      {socials.map((social) => (
                        <a
                          key={social.type}
                          className="candidate-contact-info-social-url fa-stack fa-1x"
                          href={social.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => {
                            onSocialUrlClick?.(social.type)
                          }}
                        >
                          <i className="fas fa-circle fa-stack-2x" />
                          <i className={`${social.iconClass} fa-stack-1x url-inner-icon`} />
                        </a>
                      ))}
                    </div>
                  )}
                  <div className="candidate-actions">
                    <ProfileActionsStrip
                      entity={candidate}
                      actions={actions}
                      maxActionAmount={4}
                      isDropdownEnabled
                      onAction={onAction}
                    />
                  </div>
                </div>
              </div>
            </div>
            {!viewMore ? null : <hr className="divider" />}
            {!viewMore ? null : <div className="candidate-contact-info-wrap">{contactInfo}</div>}
          </div>
          <div className="candidate-snippet-content-wrap">
            <div
              className="snippet-content nicer-scroller mac-style"
              style={{ flexBasis: maxSnippetWidth }}
              onScroll={(event) => {
                onContentScroll?.(event.currentTarget)
              }}
            >
              {viewMore ? null : <div className="candidate-contact-info-wrap">{contactInfo}</div>}
              {!loadingSummaryAndPreview ? null : (
                <div className="no-highlights-wrap inner-loading">
                  <div className="snippet-loading-container-outer">
                    <div className="snippet-loading-container">
                      <i className="fa fa-spinner fa-spin" />
                    </div>
                  </div>
                </div>
              )}
              {loadingSummaryAndPreview || hasHighlights ? null : (
                <div className="no-highlights-wrap">No highlights for this candidate</div>
              )}
              {loadingSummaryAndPreview || !hasHighlights ? null : (
                <div className="highlights-wrap">
                  <div className="highlights-scroll-wrap">
                    <div className="highlights-matching-summary-container">
                      {/* ⚠️ BOTH HALVES OF THE CONDITION MATTER, and they are not the same
                        question. `viaHunter` is where the profile was opened from and
                        `hasMatchingSummary` is whether the server answered with one — a
                        profile opened by id never shows the card, and one opened from a search
                        shows it even when the summary is empty, saying so. */}
                      {!viaHunter || !hasMatchingSummary ? null : (
                        <div className="highlight-section matching-summary-container">
                          <MatchingSummaryCard {...(alertName === undefined ? {} : { alertName })} />
                        </div>
                      )}
                      {!showAtsInfo ? null : (
                        <div className="highlight-section ats-info-container">
                          <AtsInfoCard
                            atsInfo={candidate.atsInfo ?? {}}
                            atsName={atsName}
                            {...(atsLabel === undefined ? {} : { atsLabel })}
                            cvDate={candidate.cvDate}
                            inAts={inAts}
                            onViewInAts={onViewInAts}
                          />
                        </div>
                      )}
                      {/* ⚠️ THE MASTER-PROFILE BLOCK NEEDS `viaHunter || inATS`, and `inATS` is
                        the true-by-default one — so it renders on the standalone profile. A
                        session that reported a real non-ATS host hides all three cards. */}
                      {!(viaHunter || inAts) ? null : (
                        <div className="extension-snippet-master-profile highlight-section">
                          {experience.length === 0 ? null : (
                            <div className="highlight-section">
                              <ProfileExperienceSection
                                experience={experience}
                                entityAlertInfo={candidate}
                                relevantDaysForEnriched={relevantDaysForEnriched}
                              />
                            </div>
                          )}
                          {education.length === 0 ? null : (
                            <div className="highlight-section">
                              <ProfileEducationSection education={education} />
                            </div>
                          )}
                          {skills.length === 0 ? null : (
                            <div className="highlight-section">
                              <ProfileSkillsSection skills={skills} />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            {!viewMore ? null : <div className="snippet-view-wrap">{children}</div>}
          </div>
        </div>
      </div>
    </SnippetHost>
  )
}
